import React, { useEffect } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import type { OnboardingSubject } from '../onboardingSlice';
import { OnboardingActions } from '../onboardingSlice';
import useOnboardingData from '../useOnboardingData';
import type { TutorialStep } from './TutorialModal';
import TutorialModal from './TutorialModal';
import TutorialProgress from './TutorialProgress';

interface TutorialProps {
  steps: (TutorialStep & {
    beacons?: string[];
    onActive?: () => void;
  })[];
  name: OnboardingSubject;
}

const Tutorial = ({ steps, name }: TutorialProps) => {
  const dispatch = useAppDispatch();

  const onboardingData = useOnboardingData(name);
  const { onUpdateStep } = onboardingData;

  const handleComplete = () => {
    dispatch(OnboardingActions.setActiveTutorial(null));
    return onboardingData.onComplete();
  };

  const handleSkip = () => {
    dispatch(OnboardingActions.setActiveTutorial(null));
    return onboardingData.onSkip();
  };

  const stepIndex = onboardingData.onboarding
    ? onboardingData.onboarding.step
    : 0;

  const step = steps[stepIndex];

  useEffect(() => {
    if (!step) {
      return undefined;
    }

    if (step.onActive) {
      step.onActive();
    }

    dispatch(OnboardingActions.setActiveBeacons(step.beacons));
    return () => {
      dispatch(OnboardingActions.setActiveBeacons([]));
    };
  }, [step, dispatch]);

  if (!step) {
    return null;
  }

  const commonProps = { steps, stepIndex };

  const initialStepActionProp = {
    onSecondaryClick: handleSkip,
    secondaryButtonText: 'No, thanks',
  };

  if (stepIndex === 0) {
    return (
      <TutorialModal
        onPrimaryClick={() => onUpdateStep(1)}
        primaryButtonText="Let's go"
        {...initialStepActionProp}
        {...commonProps}
      />
    );
  }

  if (stepIndex === steps.length - 1) {
    return (
      <TutorialModal
        onPrimaryClick={handleComplete}
        onSecondaryClick={() => onUpdateStep(stepIndex - 1)}
        primaryButtonText="Done"
        secondaryButtonText="Back"
        additionalControls={
          <Button variant="link" onClick={() => onUpdateStep(0)}>
            Restart
          </Button>
        }
        {...commonProps}
      />
    );
  }

  return (
    <TutorialModal
      additionalControls={
        <TutorialProgress total={steps.length - 2} at={stepIndex - 1} />
      }
      onPrimaryClick={() => onUpdateStep(stepIndex + 1)}
      onSecondaryClick={() => onUpdateStep(stepIndex - 1)}
      primaryButtonText="Continue"
      secondaryButtonText="Back"
      {...commonProps}
    />
  );
};

export default Tutorial;
